import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { WorkerLayout } from '../../components/layouts/WorkerLayout';
import { ClipboardPlus, User, Clock, Loader2, Check, Plus, MapPin, Phone, AlertTriangle, Send } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { PhotoCapture } from '../../components/ui/photo-capture';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';

const AdHocReport = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [clients, setClients] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [reportType, setReportType] = useState('normal'); // 'normal' or 'incidencia'
  const [showBeforeAfter, setShowBeforeAfter] = useState(false); // Toggle for before/after values
  const [sendEmail, setSendEmail] = useState(false);
  const [linkedIncidentId, setLinkedIncidentId] = useState('');
  const [incidents, setIncidents] = useState([]);
  
  // New client modal
  const [showNewClientModal, setShowNewClientModal] = useState(false);
  const [newClient, setNewClient] = useState({ name: '', address: '', phone: '' });
  const [savingClient, setSavingClient] = useState(false);
  
  const startedAt = useRef(new Date().toISOString()).current;
  
  const [formData, setFormData] = useState({
    client_id: '',
    checklist: [],
    parameters: [],
    materials_used: [],
    observations: '',
    photos: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [clientsRes, materialsRes, templatesRes, incidentsRes] = await Promise.all([
        api.get('/clients'),
        api.get('/materials'),
        api.get('/report-templates'),
        api.get('/work-reports/incidents')
      ]);
      setClients(clientsRes.data);
      setMaterials(materialsRes.data);
      setTemplates(templatesRes.data);
      setIncidents(incidentsRes.data || []);
      
      // Set default template
      const defaultTemplate = templatesRes.data.find(t => t.is_default) || templatesRes.data[0];
      if (defaultTemplate) {
        setSelectedTemplateId(defaultTemplate.id);
        applyTemplate(defaultTemplate);
      }
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const applyTemplate = (template) => {
    if (!template) return;
    setFormData(prev => ({
      ...prev,
      checklist: (template.checklist_items || []).map(item => ({ item, completed: false })),
      parameters: (template.parameters || []).map(p => ({ 
        name: p.name, 
        value: '', 
        value_before: '',
        value_after: '',
        unit: p.unit 
      }))
    }));
  };

  const handleCreateQuickClient = async () => {
    if (!newClient.name.trim() || !newClient.address.trim()) {
      toast.error('Nombre y dirección son obligatorios');
      return;
    }
    
    setSavingClient(true);
    try {
      const res = await api.post('/clients/quick', {
        name: newClient.name.trim(),
        address: newClient.address.trim(),
        phone: newClient.phone.trim() || null
      });
      
      // Add new client to list and select it
      setClients(prev => [...prev, res.data]);
      setFormData(prev => ({ ...prev, client_id: res.data.id }));
      setShowNewClientModal(false);
      setNewClient({ name: '', address: '', phone: '' });
      toast.success('Cliente creado correctamente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear cliente');
    } finally {
      setSavingClient(false);
    }
  };

  const handleTemplateChange = (templateId) => {
    setSelectedTemplateId(templateId);
    const template = templates.find(t => t.id === templateId);
    if (template) {
      applyTemplate(template);
    }
  };

  const handleChecklistChange = (index, checked) => {
    const newChecklist = [...formData.checklist];
    newChecklist[index].completed = checked;
    setFormData({ ...formData, checklist: newChecklist });
  };

  const handleParameterChange = (index, value, field = 'value') => {
    const newParams = [...formData.parameters];
    newParams[index][field] = value;
    setFormData({ ...formData, parameters: newParams });
  };

  const handleMaterialChange = (materialId, quantity) => {
    const existing = formData.materials_used.find(m => m.material_id === materialId);
    if (existing) {
      if (quantity <= 0) {
        setFormData({
          ...formData,
          materials_used: formData.materials_used.filter(m => m.material_id !== materialId)
        });
      } else {
        setFormData({
          ...formData,
          materials_used: formData.materials_used.map(m =>
            m.material_id === materialId ? { ...m, quantity } : m
          )
        });
      }
    } else if (quantity > 0) {
      const material = materials.find(m => m.id === materialId);
      setFormData({
        ...formData,
        materials_used: [...formData.materials_used, {
          material_id: materialId,
          name: material?.name || '',
          quantity,
          unit: material?.unit || ''
        }]
      });
    }
  };

  const handlePhotosChange = (newPhotos) => {
    setFormData(prev => ({
      ...prev,
      photos: newPhotos
    }));
  };

  const handleSubmit = async () => {
    if (!formData.client_id) {
      toast.error('Selecciona un cliente');
      return;
    }
    
    setSubmitting(true);
    try {
      // Transform checklist to match backend model
      const transformedChecklist = formData.checklist.map(item => ({
        label: item.item,
        checked: item.completed
      }));
      
      // Transform parameters to match backend model
      const transformedParameters = formData.parameters.map(p => ({
        name: p.name,
        value: showBeforeAfter ? '' : p.value,  // If using before/after, main value is empty
        value_before: showBeforeAfter ? p.value_before : null,
        value_after: showBeforeAfter ? p.value_after : null,
        unit: p.unit || ''
      }));
      
      await api.post('/work-reports', {
        client_id: formData.client_id,
        checklist: transformedChecklist,
        parameters: transformedParameters,
        materials_used: formData.materials_used,
        observations: formData.observations,
        photos: formData.photos,
        started_at: startedAt,
        report_type: reportType,
        send_email: sendEmail,
        linked_incident_id: linkedIncidentId || null
      });
      toast.success(reportType === 'incidencia' ? 'Incidencia registrada' : 'Parte enviado correctamente');
      navigate('/worker/history');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar parte');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <WorkerLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600"></div>
        </div>
      </WorkerLayout>
    );
  }

  return (
    <WorkerLayout>
      <div className="space-y-6 max-w-2xl mx-auto pb-24 px-4" data-testid="adhoc-report-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ClipboardPlus className="w-6 h-6 text-sky-500" />
            Parte de Última Hora
          </h1>
          <p className="text-slate-500">Crear un parte sin cita previa</p>
          <p className="text-sm text-sky-600 mt-1 flex items-center gap-1">
            <Clock className="w-4 h-4" />
            Inicio: {new Date(startedAt).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>

        {/* Client Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center justify-between">
              <span className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Cliente
              </span>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setShowNewClientModal(true)}
                data-testid="new-client-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Nuevo
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Select 
              value={formData.client_id} 
              onValueChange={(v) => setFormData({ ...formData, client_id: v })}
            >
              <SelectTrigger data-testid="select-client">
                <SelectValue placeholder="Seleccionar cliente..." />
              </SelectTrigger>
              <SelectContent>
                {clients.map(client => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.name} - {client.address}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Report Type Selection */}
        <Card className={reportType === 'incidencia' ? 'border-red-300 bg-red-50' : ''}>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className={`w-5 h-5 ${reportType === 'incidencia' ? 'text-red-600' : 'text-slate-400'}`} />
              Tipo de Parte
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-3">
              <Button
                type="button"
                variant={reportType === 'normal' ? 'default' : 'outline'}
                className={reportType === 'normal' ? 'bg-sky-600 hover:bg-sky-700' : ''}
                onClick={() => setReportType('normal')}
              >
                Parte Normal
              </Button>
              <Button
                type="button"
                variant={reportType === 'incidencia' ? 'default' : 'outline'}
                className={reportType === 'incidencia' ? 'bg-red-600 hover:bg-red-700' : ''}
                onClick={() => setReportType('incidencia')}
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                Incidencia
              </Button>
            </div>
            {reportType === 'incidencia' && (
              <p className="text-sm text-red-700">
                Las incidencias se muestran en rojo y sirven para reportar problemas o cosas que están mal. Describe el fallo en las observaciones y adjunta fotos.
              </p>
            )}
            
            {/* Link to existing incident (only for normal/repair reports) */}
            {reportType === 'normal' && incidents.length > 0 && (
              <div className="pt-2 border-t space-y-2">
                <Label className="text-sm font-medium flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500" />
                  Vincular con incidencia
                </Label>
                <p className="text-xs text-slate-500">Si este parte es una reparación de una incidencia anterior, selecciónala aquí</p>
                <Select value={linkedIncidentId || "none"} onValueChange={(v) => setLinkedIncidentId(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="select-linked-incident">
                    <SelectValue placeholder="Sin vincular" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin vincular</SelectItem>
                    {incidents.filter(i => !i.linked_repair_id).map(inc => (
                      <SelectItem key={inc.id} value={inc.id}>
                        {inc.client_name} - {inc.completed_at ? new Date(inc.completed_at).toLocaleDateString('es-ES') : ''} - {(inc.observations || '').slice(0, 40)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Before/After toggle */}
            <div className="flex items-center justify-between pt-2 border-t">
              <div>
                <Label className="text-sm font-medium">Valores Antes/Después</Label>
                <p className="text-xs text-slate-500">Registrar parámetros antes y después del tratamiento</p>
              </div>
              <Switch
                checked={showBeforeAfter}
                onCheckedChange={setShowBeforeAfter}
              />
            </div>
          </CardContent>
        </Card>

        {/* Template Selection */}
        {templates.length > 0 && (
          <Card>
            <CardContent className="pt-6">
              <Label>Plantilla</Label>
              <Select value={selectedTemplateId} onValueChange={handleTemplateChange}>
                <SelectTrigger className="mt-2" data-testid="select-template">
                  <SelectValue placeholder="Seleccionar plantilla..." />
                </SelectTrigger>
                <SelectContent>
                  {templates.map(template => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name} {template.is_default && '(Por defecto)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>
        )}

        {/* Checklist */}
        {formData.checklist.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Tareas</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {formData.checklist.map((item, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <Checkbox
                    id={`check-${idx}`}
                    checked={item.completed}
                    onCheckedChange={(checked) => handleChecklistChange(idx, checked)}
                  />
                  <label htmlFor={`check-${idx}`} className="text-sm cursor-pointer flex-1">
                    {item.item}
                  </label>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Parameters */}
        {formData.parameters.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Parámetros del Agua</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {showBeforeAfter ? (
                // Before/After mode
                formData.parameters.map((param, idx) => (
                  <div key={idx} className="space-y-2 p-3 bg-slate-50 rounded-lg">
                    <Label className="text-sm font-medium">{param.name}</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-slate-500">Antes</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            step="0.1"
                            value={param.value_before || ''}
                            onChange={(e) => handleParameterChange(idx, e.target.value, 'value_before')}
                            placeholder="0.0"
                            className="w-full"
                          />
                          <span className="text-xs text-slate-500">{param.unit}</span>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Después</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            step="0.1"
                            value={param.value_after || ''}
                            onChange={(e) => handleParameterChange(idx, e.target.value, 'value_after')}
                            placeholder="0.0"
                            className="w-full"
                          />
                          <span className="text-xs text-slate-500">{param.unit}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                // Normal mode
                formData.parameters.map((param, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <Label className="min-w-[140px] text-sm">{param.name}</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={param.value}
                      onChange={(e) => handleParameterChange(idx, e.target.value)}
                      placeholder="0.0"
                      className="w-24"
                    />
                    <span className="text-sm text-slate-500">{param.unit}</span>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        )}

        {/* Materials */}
        {materials.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Materiales Usados</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {materials.slice(0, 10).map(material => {
                const used = formData.materials_used.find(m => m.material_id === material.id);
                return (
                  <div key={material.id} className="flex items-center justify-between">
                    <span className="text-sm">{material.name}</span>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="0"
                        value={used?.quantity || ''}
                        onChange={(e) => handleMaterialChange(material.id, parseFloat(e.target.value) || 0)}
                        placeholder="0"
                        className="w-20 h-8 text-sm"
                      />
                      <span className="text-xs text-slate-500 w-8">{material.unit}</span>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Observations */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Observaciones</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={formData.observations}
              onChange={(e) => setFormData({ ...formData, observations: e.target.value })}
              placeholder="Notas adicionales, problemas detectados..."
              rows={4}
              data-testid="observations-textarea"
            />
          </CardContent>
        </Card>

        {/* Photos */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Fotos</CardTitle>
          </CardHeader>
          <CardContent>
            <PhotoCapture 
              photos={formData.photos}
              onPhotosChange={handlePhotosChange}
              maxPhotos={10}
              disabled={submitting}
            />
          </CardContent>
        </Card>

        {/* Send Email Toggle */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium flex items-center gap-2">
                  <Send className="w-4 h-4 text-sky-500" />
                  Enviar al cliente por email
                </Label>
                <p className="text-xs text-slate-500 mt-1">Se enviará el PDF del parte al email del cliente</p>
              </div>
              <Switch
                checked={sendEmail}
                onCheckedChange={setSendEmail}
                data-testid="send-email-toggle"
              />
            </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t shadow-lg z-20">
          <Button
            className="w-full h-12 text-lg"
            onClick={handleSubmit}
            disabled={submitting || !formData.client_id}
            data-testid="submit-report-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Check className="w-5 h-5 mr-2" />
                Enviar Parte
              </>
            )}
          </Button>
        </div>

        {/* New Client Modal */}
        <Dialog open={showNewClientModal} onOpenChange={setShowNewClientModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Nuevo Cliente</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  Nombre *
                </Label>
                <Input
                  value={newClient.name}
                  onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                  placeholder="Nombre del cliente"
                  data-testid="new-client-name"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  Dirección *
                </Label>
                <Input
                  value={newClient.address}
                  onChange={(e) => setNewClient({ ...newClient, address: e.target.value })}
                  placeholder="Dirección de la piscina"
                  data-testid="new-client-address"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <Phone className="w-4 h-4" />
                  Teléfono (opcional)
                </Label>
                <Input
                  value={newClient.phone}
                  onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                  placeholder="Teléfono de contacto"
                  data-testid="new-client-phone"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNewClientModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleCreateQuickClient} disabled={savingClient}>
                {savingClient ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  'Crear Cliente'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </WorkerLayout>
  );
};

export default AdHocReport;
